import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { Search, X, FileText, LayoutDashboard, Zap, BarChart3 } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useSearch } from "../SearchProvider";
import { searchIndex } from "../data";
import { filterSearchItems, resolveSearchLabel } from "../utils";
import { cn } from "@/shared/lib/cn";

/** Ce que l'on peut chercher, affiché tour à tour dans le champ vide. */
const SEARCH_HINTS = ["une page", "une facture", "un assuré", "une demande", "un prestataire"];

type SearchBarProps = {
  className?: string;
};

export function SearchBar({ className }: SearchBarProps) {
  const { t } = useTranslation();
  const { query, setQuery, isOpen, open, close, inputRef } = useSearch();
  const containerRef = useRef<HTMLDivElement>(null);

  const results = filterSearchItems(searchIndex, query, t);
  const reduceMotion = useReducedMotion();
  const [focused, setFocused] = useState(false);
  const [hintIndex, setHintIndex] = useState(0);
  // L'indice ne défile que champ vide et inactif ; figé si l'utilisateur réduit les animations.
  const showHint = !query && !focused;

  useEffect(() => {
    if (!showHint || reduceMotion) return;
    const id = window.setInterval(() => setHintIndex((i) => (i + 1) % SEARCH_HINTS.length), 2800);
    return () => window.clearInterval(id);
  }, [showHint, reduceMotion]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, close]);

  return (
    <>
      {isOpen && query.trim() !== "" && (
        <button
          type="button"
          aria-label="Close search"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={close}
        />
      )}

      <div
        ref={containerRef}
        className={cn(
          "relative z-50 flex flex-1",
          isOpen && "fixed inset-x-3 top-3 sm:inset-x-4 sm:top-4 lg:static lg:inset-auto",
          className,
        )}
      >
        <div className="group/search relative w-full">
          {/* Contour en dégradé lime → sarcelle, révélé en fondu au focus. */}
          <span
            className="pointer-events-none absolute -inset-px rounded-[13px] bg-[linear-gradient(110deg,#a6f21b,#2dd4bf_55%,#a6f21b)] opacity-0 transition-opacity duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-focus-within/search:opacity-100"
            aria-hidden="true"
          />
          <span
            className="pointer-events-none absolute inset-0 rounded-xl shadow-[0_8px_24px_-12px_rgb(166_242_27/0.6)] opacity-0 transition-opacity duration-200 group-focus-within/search:opacity-100"
            aria-hidden="true"
          />
          <Search
            size={18}
            className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-muted transition-colors duration-150 group-focus-within/search:text-[#4d7c0f] dark:group-focus-within/search:text-accent"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!isOpen) open();
            }}
            onFocus={() => {
              setFocused(true);
              open();
            }}
            onBlur={() => setFocused(false)}
            aria-label={t.search.placeholder}
            // Indice animé à la place du placeholder natif quand le champ est vide et inactif.
            placeholder={showHint ? "" : t.search.placeholder}
            className="relative h-11 w-full rounded-xl border border-border bg-card pl-11 pr-16 text-sm text-foreground outline-none transition-[border-color] duration-150 placeholder:text-muted hover:border-primary/30 focus:border-transparent sm:pl-12 sm:pr-20"
          />
          {showHint ? (
            <span className="pointer-events-none absolute inset-y-0 left-11 z-10 flex items-center gap-1 overflow-hidden text-sm text-muted sm:left-12" aria-hidden="true">
              Rechercher
              <span className="relative inline-flex h-5 overflow-hidden">
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.span
                    key={SEARCH_HINTS[hintIndex]}
                    className="whitespace-nowrap font-medium text-foreground/70"
                    initial={{ transform: "translateY(100%)", opacity: 0 }}
                    animate={{ transform: "translateY(0%)", opacity: 1 }}
                    exit={{ transform: "translateY(-100%)", opacity: 0 }}
                    transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
                  >
                    {SEARCH_HINTS[hintIndex]}…
                  </motion.span>
                </AnimatePresence>
              </span>
            </span>
          ) : null}
          <div className="absolute right-3 top-1/2 z-10 flex -translate-y-1/2 items-center gap-1">
            {query ? (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="flex h-6 w-6 items-center justify-center rounded-md text-muted transition-colors hover:bg-accent/20 hover:text-foreground"
                aria-label="Effacer la recherche"
              >
                <X size={14} />
              </button>
            ) : (
              <kbd className="hidden rounded-md border border-accent/50 bg-accent/20 px-1.5 py-0.5 text-[10px] font-semibold text-[#3f6212] dark:text-accent lg:inline">
                ⌘K
              </kbd>
            )}
          </div>
        </div>

        {/* Résultats seulement une fois la saisie commencée : rien ne s'ouvre au simple clic. */}
        {isOpen && query.trim() !== "" && (
          <div className="theme-card theme-card-static absolute left-0 right-0 top-full z-50 mt-2 max-h-[400px] overflow-y-auto p-1">
            {results.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted">
                {t.search.noResults}
              </div>
            ) : (
              <ul className="p-2">
                {results.map((item) => {
                  const Icon =
                    item.categoryKey === "search.indicators"
                      ? BarChart3
                      : item.categoryKey === "search.actions"
                        ? Zap
                        : item.type === "page"
                          ? LayoutDashboard
                          : FileText;
                  const category = resolveSearchLabel(t, item.categoryKey);

                  return (
                    <li key={item.id}>
                      <Link
                        to={item.href}
                        onClick={close}
                        className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-accent/20"
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft">
                          <Icon size={16} className="text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground">
                            {resolveSearchLabel(t, item.labelKey)}
                          </p>
                          <p className="text-xs text-muted">{category}</p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </>
  );
}
